import React, {useMemo, useState} from 'react'
import NextLink from 'next/link'
import {Accordion, AccordionItem} from '@heroui/react'
import Link from 'next/link'

import ButtonWrapper from '@/components/UI/ButtonWrapper/ButtonWrapper'
import {Body4} from '@/components/UI/Typography'

import Box from '../UI/Box/Box'
import Icon from '../UI/Icon/Icon'
import MenuLink from '../UI/MenuLink/MenuLink'
import {
  advisorMenuContent,
  clientMenuContent,
} from '../AdvisorAccount/SideMenu/constants'
import {CollapseMenuContentProps} from './types'
import {
  CollapseMenuContent as CollapseMenuContentType,
  generateCollapseMenuItems,
  generateMegaMenuItems,
} from '../../src/shared/utils/categories'

import {cn} from '@/src/shared/utils/twMerge'

const createCollapseMenu = (
  menu: CollapseMenuContentType[],
  onCloseMenu?: () => void
) => {
  const handleLinkClick = () => {
    if (onCloseMenu) {
      onCloseMenu()
    }
  }

  return (
    <Box className="flex-col gap-6 p-4 pt-8 w-full">
      <NextLink href={menu[0].href} onClick={handleLinkClick}>
        <Box className="items-center gap-2">
          <Icon name={menu[0].icon} className="fill-primary" w={20} h={20} />
          <span className="text-gray-700 font-medium">{menu[0].title}</span>
        </Box>
      </NextLink>
      <Box className="flex flex-col w-4/5">
        <span className="text-primary font-semibold">Kategoriler</span>
        <Accordion showDivider={false} selectionMode="single">
          {menu.slice(1, 4).map((element, index) => (
            <AccordionItem
              title={
                <Box className="gap-2">
                  <Icon
                    name={element.icon}
                    className="fill-primary"
                    w={20}
                    h={20}
                  />
                  {element.title}
                </Box>
              }
              key={index}>
              {element.dropdown?.map((element2, index) => (
                <Accordion
                  selectionMode="single"
                  key={`${element2.title}-${index}`}>
                  <AccordionItem className="pl-4" title={element2.title}>
                    <Box className="flex-col gap-2 ml-4">
                      {element2.dropdown?.map((element3, index) => (
                        <Link
                          key={index}
                          href={element3.href}
                          className="py-2"
                          onClick={handleLinkClick}>
                          {element3.title}
                        </Link>
                      ))}
                    </Box>
                  </AccordionItem>
                </Accordion>
              ))}
            </AccordionItem>
          ))}
        </Accordion>
      </Box>
      <Box className="flex-col gap-6">
        <span className="text-primary font-semibold">Keşfet ve Katıl</span>
        <Box className="pb-8 flex-col gap-8">
          {menu.slice(4).map((element, index) => (
            <NextLink key={index} href={element.href} onClick={handleLinkClick}>
              <Box className="items-center gap-2">
                <Icon
                  name={element.icon}
                  className="fill-primary"
                  w={20}
                  h={20}
                />
                <span className="text-gray-700 font-medium">
                  {element.title}
                </span>
              </Box>
            </NextLink>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

const createAccountMenu = (isAdvisor: boolean | undefined, url: string) => {
  return (
    <Box className="flex-col gap-8 p-4 pt-8">
      {isAdvisor && (
        <Box className="flex-col gap-4">
          <span className="text-primary font-semibold">Danışman Hesabım</span>
          {advisorMenuContent.map((element, index) => {
            return (
              <NextLink key={index} href={element.href}>
                <Box className="items-center gap-2">
                  <Icon
                    name={element.iconName}
                    w={20}
                    h={20}
                    className="fill-primary"
                  />
                  <span
                    className={cn(
                      'font-medium',
                      element.href === url ? 'text-black' : 'text-gray-700'
                    )}>
                    {element.text}
                  </span>
                  {element.IsNew && (
                    <span className="bg-white border border-primary rounded-3xl text-primary text-xs px-1.5 py-1 ml-2 whitespace-nowrap">
                      Yeni
                    </span>
                  )}
                </Box>
              </NextLink>
            )
          })}
        </Box>
      )}
      <Box className="flex-col gap-4">
        <span className="text-primary font-semibold">Danışan Hesabım</span>
        {clientMenuContent.map((element, index) => {
          return (
            <NextLink key={index} href={element.href}>
              <Box className="items-center gap-2">
                <Icon
                  name={element.iconName}
                  className="fill-primary"
                  w={20}
                  h={20}
                />
                <span
                  className={cn(
                    'font-medium',
                    element.href === url ? 'text-black' : 'text-gray-700'
                  )}>
                  {element.text}
                </span>
              </Box>
            </NextLink>
          )
        })}
      </Box>
    </Box>
  )
}

export const CollapseMenuContent = React.memo(
  ({isAdvisor, status}: CollapseMenuContentProps) => {
    const [selectedTab, setSelectedTab] = useState<boolean>(true)
    const menu = useMemo(
      () => generateCollapseMenuItems(status ?? ''),
      [status]
    )
    return (
      <Box className="flex-col pb-20">
        <Box className="gap-6">
          <ButtonWrapper
            onClick={() => setSelectedTab(true)}
            className={`rounded-none font-semibold text-md relative`}>
            Ana Menu
            {selectedTab && (
              <Box className="w-full h-[2px] bg-primary absolute bottom-0" />
            )}
          </ButtonWrapper>
          <ButtonWrapper
            onClick={() => setSelectedTab(false)}
            className={`rounded-none font-semibold text-md relative`}>
            Hesabım
            {!selectedTab && (
              <Box className="w-full h-[2px] bg-primary absolute bottom-0" />
            )}
          </ButtonWrapper>
        </Box>
        <Box className={selectedTab ? 'block' : 'hidden'}>
          {createCollapseMenu(menu)}
        </Box>
        <Box className={!selectedTab ? 'block' : 'hidden'}>
          {createAccountMenu(isAdvisor, '')}
        </Box>
      </Box>
    )
  }
)

CollapseMenuContent.displayName = 'CollapseMenuContent'

const createMegaMenu = (id: number) => {
  const megaMenuContent = generateMegaMenuItems()
  const column0 = megaMenuContent[id][0]?.map((object, index) => {
    return index === 0 ? (
      <Body4
        key={`${object.text}-${index}`}
        className="p-2 mb-2 h-12 border-b-1 border-black w-fit">
        {object?.text}
      </Body4>
    ) : (
      <MenuLink
        as={Link}
        key={`${object.text}-${index}`}
        className="text-gray-600 text-base bg-transparent justify-start"
        href={object?.href}>
        {object?.text}
      </MenuLink>
    )
  })
  const column1 = megaMenuContent[id][1]?.map((object, index) => {
    return index === 0 ? (
      <Body4
        key={`${object.text}-${index}`}
        className="p-2 mb-2 h-12 border-b-1 border-black w-fit">
        {object?.text}
      </Body4>
    ) : (
      <MenuLink
        as={Link}
        key={`${object.text}-${index}`}
        className="text-gray-600 text-base bg-transparent justify-start"
        href={object?.href}>
        {object?.text}
      </MenuLink>
    )
  })
  const column2 = megaMenuContent[id][2]?.map((object, index) => {
    return index === 0 ? (
      <Body4
        key={`${object.text}-${index}`}
        className="p-2 mb-2 h-12 border-b-1 border-black w-fit">
        {object?.text}
      </Body4>
    ) : (
      <MenuLink
        as={Link}
        key={`${object.text}-${index}`}
        className="text-gray-600 text-base bg-transparent justify-start"
        href={object?.href}>
        {object?.text}
      </MenuLink>
    )
  })
  return {
    column0,
    column1,
    column2,
  }
}

const megaMenuContent = {
  menu0: createMegaMenu(1),
  menu1: createMegaMenu(2),
  menu2: createMegaMenu(3),
}

export default megaMenuContent
